// External
import { AfterViewInit, Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Internal
import { ConfigService, LogoutType } from '../../common/config/config.service';
import { ValuesService } from '../../common/values/values.service';
import { LinksService } from '../../common/links/links.service';
import { MessageService } from '../../common/services/core/message.service';
import { LanguageService } from '../../common/services/core/language.service';
import { UsefulService } from '../../common/services/global/useful/useful.service';
import { ProductsConfigService } from '../../common/config/products.config.service';
import { LoginLinksService } from '../../common/services/global/loginLinks/loginLinks.service';
import { ModalName, ModalRoutelessService } from '../../common/components/ui/ui-modal-routeless/modal.routeless.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { CampaignMediaIdentifiers, CampaignNames, CampaignParameters } from '../../common/services/core/AdobeParams.model';
import { BuyLinksService } from '../../common/links/buyLinks.service';
import { Outlet, OutletStatus, OutletsStatusService } from '../../common/services/core/outlets.status.service';
import { ModalSize } from '../../common/services/global/modal/Modal.model';
import { AdobeDataLayerService } from '../../common/services/core/adobe.datalayer.service';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, AfterViewInit {

    showPage = false;
    lang = '';

    year: number;
    isMobile = false;
    private onDestroy$: Subject<void> = new Subject<void>();

    // page links
    links: any;

    // new
    manufacturerServices = [
        {
            title: 'hp.capabilities.protection.title',
            descr: 'hp.capabilities.protection.description',
            icon: 'local-protection',
            iconType: 'feature',
            show: () => this.configService.getHomeSectionCapabilityDeviceProtection()
        },
        {
            title: 'hp.capabilities.remote.title',
            descr: 'hp.capabilities.remote.description',
            icon: 'remote-action',
            iconType: 'action',
            show: () => this.configService.getHomeSectionCapabilityRemoteActions()
        },
        {
            title: 'hp.capabilities.install.title',
            descr: 'hp.capabilities.install.description',
            icon: 'install-protection',
            iconType: 'action',
            show: () => this.configService.getHomeSectionCapabilityInstallProtection()
        },
        {
            title: 'hp.capabilities.services.title',
            descr: 'hp.capabilities.services.description',
            icon: 'subscriptions',
            iconType: 'feature',
            show: () => this.configService.getHomeSectionCapabilityManageServices()
        },
        {
            title: 'hp.capabilities.parental.title',
            descr: 'hp.capabilities.parental.description',
            icon: 'product-Parental',
            iconType: 'feature',
            show: () => this.configService.getHomeSectionCapabilityParentalControl()
        },
        {
            title: 'hp.capabilities.insights.title',
            descr: 'hp.capabilities.insights.description',
            icon: 'safety-tips',
            iconType: 'feature',
            show: () => this.configService.getHomeSectionCapabilityIndustryInsights()
        }
    ];

    topProducts = [
        {
            subtitle: 'hp.product.PS.subtitle',
            name: this.productsConfigService.getProductName(this.valuesService.productNamePS),
            descr: 'hp.product.PS.description',
            ill: this.valuesService.centralImages.illustrationCelebrate,
            cta: () => this.goToSolutionPage(this.valuesService.productNamePS, CampaignNames.HOMEPAGE_PS)
        },
        {
            subtitle: 'hp.product.TS.subtitle',
            name: this.productsConfigService.getProductName(this.valuesService.productNameTS),
            descr: 'hp.product.TS.description',
            ill: this.valuesService.centralImages.protectAllDevices,
            cta: () => this.goToSolutionPage(this.valuesService.productNameTS, CampaignNames.HOMEPAGE_TS)
        },
        {
            subtitle: 'hp.product.DIP.subtitle',
            name: this.productsConfigService.getProductName(this.valuesService.productNameDIP),
            descr: 'hp.product.DIP.description',
            ill: this.valuesService.centralImages.dipProtection,
            cta: () => this.goToSolutionPage(this.valuesService.productNameDIP, CampaignNames.HOMEPAGE_DIP)
        }
    ];

    topServices = [
        {
            name: this.productsConfigService.getProductName(this.valuesService.productNameVPNBasic),
            descr: 'hp.product.VPNBasic.description',
            icon: 'vpn',
            cta: () => this.goToSolutionPage(this.valuesService.productNameVPNBasic, CampaignNames.HOMEPAGE_VPN)
        },
        {
            name: this.productsConfigService.getProductName(this.valuesService.productNameIDTP),
            descr: 'hp.product.IDTP.description',
            icon: 'idtheftprotection',
            cta: () => this.goToSolutionPage(this.valuesService.productNameIDTP, CampaignNames.HOMEPAGE_IDP)
        },
        {
            name: this.productsConfigService.getProductName(this.valuesService.productNamePasswordManager),
            descr: 'hp.product.PasswordManager.description',
            icon: 'passwordmanager',
            cta: () => this.goToSolutionPage(this.valuesService.productNamePasswordManager, CampaignNames.HOMEPAGE_PM)
        }
    ];

    constructor(
        public configService: ConfigService,
        private modalRoutelessService: ModalRoutelessService,
        public translateService: TranslateService,
        public valuesService: ValuesService,
        public messageService: MessageService,
        public linksService: LinksService,
        public usefulService: UsefulService,
        private readonly languageService: LanguageService,
        public readonly productsConfigService: ProductsConfigService,
        private readonly loginLinksService: LoginLinksService,
        private readonly adobeDataLayerService: AdobeDataLayerService,
        private readonly deviceDetectorService: DeviceDetectorService,
        private readonly buyLinksService: BuyLinksService,
        private readonly outletsStatusService: OutletsStatusService,
    ) {
        this.initLanguage();
        this.year = new Date().getFullYear();
        this.showPage = true;

        this.messageService.getMessage()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(message => {
            if (message.text === this.valuesService.events.languageChange) {
                this.initLanguage();
            }
        });
    }

    ngOnInit() {
        this.isMobile = this.deviceDetectorService.isMobile();
    }

    ngAfterViewInit() {
        this.outletsStatusService.setStatus(Outlet.PAGE, OutletStatus.DONE);
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    initLanguage() {
        this.lang = this.languageService.getLang();

        this.links = {
            legal: this.linksService.getLegalTermsLink(),
            privacy: this.linksService.getPrivacyPolicyLink(),
            support: this.linksService.support()
        };
    }

    goToLoginPage() {
        window.location.href = this.loginLinksService.loginPage(false, true, LogoutType.REDIRECT_TO_LOGIN_PAGE);
    }

    goToCreateAccount() {
        window.location.href = this.loginLinksService.createAccountPage(true);
    }

    goToSupport() {
        window.location.href = this.links.support;
    }

    goToGooglePlay(event) {
        event.preventDefault();
        window.open(this.adobeDataLayerService.addUtmSource(this.linksService.googlePlayLink, this.valuesService.googlePlayUtmSourceParam), '_blank');
    }

    goToSolutionPage(productName, campaignName) {
        const campaignParams: CampaignParameters = {
            internal: true,
            mediaId: CampaignMediaIdentifiers.DETAILS,
            campaignName: campaignName
        };
        const extraParams = {
            lang: this.languageService.getLang(),
        };
        const link = this.buyLinksService.getProductSolutionLink(productName, extraParams, campaignParams)
        window.open(link, '_blank');
    }

    // opens change-language-modal type
    openLanguageModal() {
        this.modalRoutelessService.open(ModalName.changeLanguageModal, {size: ModalSize.LG}, null);
    }

}
